/* eslint-disable react-hooks/exhaustive-deps */
import React, {useEffect, useMemo, useState} from "react"
import {useTranslation} from "react-i18next"
import moment from "moment"
import {KlassappTable, KlassappTableHeader} from "uiKit"
import {BaseService, studentActivitiesService} from "services"
import {Icon} from "components/Icon"
import {KlassappTableHOC} from "HOC"
import {formatDateOnly, getFullName, handleError, toastError, toastSuccess} from "helpers"
import {exportCsv, exportExcel} from "helpers/export-table"
import {ExportFileType} from "components/ui"
import styles from "./ActivitiesTable.module.css"
import {
    useActiveTab,
    useAllActivities,
    useAllCampuses,
    useAllDepartments,
    useAllPrograms,
    useCurrentProfilePermissions,
    useModel,
    useVisible
} from "hooks"
import cx from "classnames"
import ProcessWorkflowModal from "sections/Settings/Workflows/parts/ProcessWorkflowModal"
import {Spin, Tabs, Tooltip} from "antd"
import {ActivityPopup} from "../ActivityPopup"
import WorkflowActivitiesTable from "./WorkflowActivitiesTable"
import {EVENT_STUDENT_ACTIVITY_COMPLETION_TOGGLED, StudentActivity} from "types/activity"
import {DisplayMode, KlassappTableProps} from "types/common"
import ActivityStats from "sections/shared/student/ActivitiesTable/ActivityStats"
import {Link, useHistory} from "react-router-dom"
import {TriggeredWorkflowStatus} from "types/triggered-workflow"
import {AppliedFilter, FieldOperator, FilterKey, formatDateFilterValue} from "types/filter"
import {isEmpty, pick} from "lodash"
import {emitEvent} from "helpers/eventHelper"
import {useMutation} from "@tanstack/react-query"
import CircularSpin from "components/CircularSpin"
import {
    BASE_DEPARTMENT_NAME,
    BaseDepartmentId,
    DEPARTMENTS_WITH_ACTIVITIES,
    MAP_DEPARTMENT_ID_2_KEY
} from "types/departments"
import {ADMISSION_STUDENT_TABS} from "sections/Admission/AdmissionStudent/AdmissionStudent"
import {AdmissionStudentAdmissionSubtab} from "sections/Admission/AdmissionStudent/parts"
import {FinancialAidStudentTabs} from "sections/FinancialAid/FinancialAidStudent/FinancialAidStudent"
import {FinAidStudentFinAidSubtab} from "sections/FinancialAid/FinancialAidStudent/parts/FinancialAidStudentTab/FinancialAidStudentTab"
import {RegistrarStudentTab} from "sections/academics/student/AcademicsStudent"
import {StudentServicesStudentTab} from "sections/StudentServices/StudentServicesStudent/StudentServicesStudent"
import {TabHeader} from "components/Tab"
import {Modules, useEdularModulesContext} from "@edular/modules"
import StudentActivityFilters from "components/StudentActivityFilters/StudentActivityFilters"

const {TabPane} = Tabs

type Props = KlassappTableProps & {
    studentId?: number
    departmentId?: number
    includesHoldActivities?: boolean
    addNewOpenPage?: boolean
    displayMode?: DisplayMode
}

type ActivitiesTab = {
    departmentId: number
    name: string
    tabKey: string
}

function ActivitiesTable(props: Props) {
    const {
        dispatch,
        dispatchFunc,
        page,
        total,
        pageSize,
        columns,
        data,
        allFields,
        fields,
        orderField,
        tableHeaderActions,
        isLoading,
        isLoadedTableFuncs,
        getCurrentPage,
        getCurrentData,
        getCurrentPageSize,
        departmentId,
        displayMode = "normal"
    } = props

    const {isModuleEnable} = useEdularModulesContext()

    const modules: ActivitiesTab[] = useMemo(
        () =>
            DEPARTMENTS_WITH_ACTIVITIES.filter((departmentId) => {
                switch (departmentId) {
                    case BaseDepartmentId.Admissions:
                        return isModuleEnable(Modules.Admissions)
                    case BaseDepartmentId.Academics:
                        return isModuleEnable(Modules.Academic)
                    case BaseDepartmentId.FinancialAid:
                        return isModuleEnable(Modules.FinancialAid)
                    case BaseDepartmentId.StudentAccount:
                        return isModuleEnable(Modules.StudentAccount)
                    case BaseDepartmentId.StudentServices:
                        return isModuleEnable(Modules.StudentServices)
                    case BaseDepartmentId.CareerServices:
                        return isModuleEnable(Modules.CareerServices)
                    default:
                        return false
                }
            }).map((departmentId) => ({
                departmentId,
                name: BASE_DEPARTMENT_NAME[departmentId],
                tabKey: MAP_DEPARTMENT_ID_2_KEY[departmentId]
            })),
        []
    )

    const initiallySelectedTabKey = modules.find((m) => m.departmentId === departmentId)?.tabKey

    const [activeTabKey, setActiveTabKey] = useActiveTab(initiallySelectedTabKey, "activitiesDepartment")

    const selectedDepartmentId = useMemo(
        () => (activeTabKey ? modules.find((m) => m.tabKey === activeTabKey)?.departmentId : null),
        [activeTabKey]
    )

    const {t} = useTranslation(["activities", "common"])
    const history = useHistory()
    const model = useModel()
    const userDateFormat = model.getUserDateFormat()
    const userDateTimeFormat = model.getUserDateTimeFormat()
    const userTimeFormat = model.getUserTimeFormat()
    const dataSeq = React.useRef(0)
    const currentProfilePermissions = useCurrentProfilePermissions()
    const filterKey = `${FilterKey.ActivityTable}_${departmentId || "dashboard"}` as FilterKey
    const storageData = model.getStorageFilter(filterKey)

    const activityPopup = useVisible(false)
    const [formEdit, setFormEdit] = useState(null)
    const [appliedFilter, setAppliedFilter] = useState<AppliedFilter>({
        search: storageData?.search ?? "",
        filters: storageData?.filters ?? {}
    })
    const [workflowActivity, setWorkflowActivity] = React.useState<StudentActivity | undefined>()
    const showWorkflowDetail = async (activity: StudentActivity) => {
        setWorkflowActivity(activity)
    }
    const isFullDisplay = displayMode === "normal"
    const [advancedFilter, setAdvancedFilter] = useState<"activities" | "workflowActivities" | undefined>()
    const [isExpanded, setExpanded] = useState(false)
    const {departments} = useAllDepartments()
    const {activities: allActivities} = useAllActivities()
    const {campuses} = useAllCampuses()
    const {programs} = useAllPrograms()

    const activities = useMemo(() => {
        let departmentIdToUserActivitiesFrom: number | null = null

        if (selectedDepartmentId) {
            const moduleDepartment = departments.find((dept) => dept.departmentId === selectedDepartmentId)

            departmentIdToUserActivitiesFrom = moduleDepartment?.useActivitiesFromDepartmentId
        }
        return allActivities
            .filter((activity) => {
                if (!activity.isActive) return false

                if (departmentIdToUserActivitiesFrom && activity.departmentId !== departmentIdToUserActivitiesFrom)
                    return false

                return true
            })
            .map((activity) => pick(activity, ["activityId", "name"]))
    }, [departments, allActivities, selectedDepartmentId])

    const getFields = React.useCallback(() => {
        return [
            "icon",
            t("activities.dateTime"),
            t("activities.aging"),
            t("activities.activity"),
            t("activities.student"),
            t("activities.assignedTo"),
            t("activities.completed")
        ]
    }, [])

    const getFilterMemoryKey = () => {
        return filterKey
    }

    const checkIfCanDeleteActivity = (departmentId: number | null = null): boolean => {
        return (
            currentProfilePermissions.departmentActivities.hasPermissionsToDelete(
                selectedDepartmentId || departmentId
            ) || model.isAdmin()
        )
    }

    const canDeleteActivity = useMemo(() => checkIfCanDeleteActivity(), [])

    const buildListParams = React.useCallback(
        (range: {limit: number; offset: number}) => {
            const params: any = {
                range,
                filterV2: {
                    search: appliedFilter.search,
                    filters: {
                        profileIds: props.studentId
                            ? {
                                  operator: FieldOperator.Equal,
                                  value: [props.studentId]
                              }
                            : appliedFilter.filters.profiles
                            ? {
                                  ...appliedFilter.filters.profiles,
                                  value: appliedFilter.filters.profiles.value?.map((item) => item.profileId)
                              }
                            : undefined,
                        departmentIds: selectedDepartmentId
                            ? {
                                  operator: FieldOperator.Equal,
                                  value: [selectedDepartmentId]
                              }
                            : appliedFilter.filters.departments
                            ? {
                                  ...appliedFilter.filters.departments,
                                  value: appliedFilter.filters.departments.value?.map((item) => item.departmentId)
                              }
                            : undefined,
                        activityIds: appliedFilter.filters.activities && {
                            ...appliedFilter.filters.activities,
                            value: appliedFilter.filters.activities.value?.map((item) => item.activityId)
                        },
                        campusIds: appliedFilter.filters.campuses && {
                            ...appliedFilter.filters.campuses,
                            value: appliedFilter.filters.campuses.value?.map((item) => item.id)
                        },
                        programIds: appliedFilter.filters.programs && {
                            ...appliedFilter.filters.programs,
                            value: appliedFilter.filters.programs.value?.map((item) => item.id)
                        },
                        studentStatusIds: appliedFilter.filters.studentStatusIds && {
                            ...appliedFilter.filters.studentStatusIds,
                            value: appliedFilter.filters.studentStatusIds.value?.map((item) => item.statusId)
                        },
                        assignedProfileIds: appliedFilter.filters.assignedProfiles && {
                            ...appliedFilter.filters.assignedProfiles,
                            value: appliedFilter.filters.assignedProfiles.value?.map((item) => item.profileId)
                        },
                        completedByProfileIds: appliedFilter.filters.completedBy && {
                            ...appliedFilter.filters.completedBy,
                            value: appliedFilter.filters.completedBy.value?.map((item) => item.profileId)
                        },
                        activityDate: appliedFilter.filters.activityDate && {
                            ...appliedFilter.filters.activityDate,
                            value: formatDateFilterValue(
                                appliedFilter.filters.activityDate.operator,
                                appliedFilter.filters.activityDate.value
                            )
                        },
                        completedDate: appliedFilter.filters.completedDate && {
                            ...appliedFilter.filters.completedDate,
                            value: formatDateFilterValue(
                                appliedFilter.filters.completedDate.operator,
                                appliedFilter.filters.completedDate.value
                            )
                        },
                        studentDueDate: appliedFilter.filters.studentDueDate && {
                            ...appliedFilter.filters.studentDueDate,
                            value: formatDateFilterValue(
                                appliedFilter.filters.studentDueDate.operator,
                                appliedFilter.filters.studentDueDate.value
                            )
                        },
                        studentStartDate: appliedFilter.filters.studentStartDate && {
                            ...appliedFilter.filters.studentStartDate,
                            value: formatDateFilterValue(
                                appliedFilter.filters.studentStartDate.operator,
                                appliedFilter.filters.studentStartDate.value
                            )
                        },
                        mineOnly: appliedFilter.filters.mineOnly,
                        completionStatus: appliedFilter.filters.completionStatus && {
                            ...appliedFilter.filters.completionStatus,
                            value: appliedFilter.filters.completionStatus.value?.id
                        }
                    }
                },
                linkedObjects: true,
                includesHoldActivities: isEmpty(advancedFilter) ? props.includesHoldActivities : false,
                workflowActivities: advancedFilter === "workflowActivities",
                advancedFilter
            }
            // Panel mode hides the table header/sort UI, so ignore remembered column
            // sort and use the API default (pinned first, then activityDate desc).
            if (isFullDisplay && !isEmpty(orderField)) {
                params.sort = {
                    orderBy: orderField.field,
                    orderDir: orderField.order
                }
            }
            return params
        },
        [
            appliedFilter,
            orderField?.field,
            orderField?.order,
            isFullDisplay,
            selectedDepartmentId,
            props.studentId,
            props.includesHoldActivities,
            advancedFilter
        ]
    )

    const getData = React.useCallback(async () => {
        dataSeq.current += 1
        const currentSeq = dataSeq.current
        try {
            dispatch({isLoading: true})
            const storageData = model.getStorageFilter(filterKey)
            const page = storageData?.page || getCurrentPage()
            const pageSize = storageData?.pageSize || getCurrentPageSize()
            const params = buildListParams({
                limit: pageSize,
                offset: (page - 1) * pageSize
            })
            const {data: activities, total} = await studentActivitiesService.getActivities(params)

            if (dataSeq.current !== currentSeq) return

            dispatch({
                data: activities.map((activity) => ({
                    ...activity,
                    id: activity.studentActivityId,
                    warning: !!activity.isHoldActivity,
                    expandedRowClass: styles.expandedRow
                })),
                total
            })

            if (total < (page - 1) * pageSize) {
                props.onChangePage(1)
            }
        } catch (error) {
            handleError(error)
        } finally {
            dispatch({isLoading: false})
        }
    }, [buildListParams, departmentId, filterKey])

    const onClearFilter = React.useCallback(() => {
        setAppliedFilter({filters: {}, search: ""})
    }, [])

    const onApplyFilter = React.useCallback(
        (payload: AppliedFilter) => {
            setAppliedFilter(payload)
            model.updateStorageFilter(filterKey, payload)
        },
        [filterKey]
    )

    const [isExporting, setIsExporting] = useState(false)

    const formatActivityExportValue = React.useCallback(
        (activity: StudentActivity, columnTitle: string) => {
            const flatten = (value: string) => value.replace(/\r\n|\n|\r/g, " ")
            let text = ""

            if (columnTitle === t("activities.dateTime")) {
                text = activity.activityDate ? moment(activity.activityDate).format(userDateTimeFormat) : ""
            } else if (columnTitle === t("activities.aging")) {
                text = activity.activityDate ? moment(activity.activityDate).fromNow(true) : ""
            } else if (columnTitle === t("activities.activity")) {
                text = activity.activity?.name ?? ""
            } else if (columnTitle === t("activities.student")) {
                text = getFullName(activity.student)
            } else if (columnTitle === t("activities.assignedTo")) {
                text = (activity.assignedProfiles ?? [])
                    .map((profile) => `${getFullName(profile)}${profile.title ? ` (${profile.title})` : ""}`)
                    .join(", ")
            } else if (columnTitle === t("activities.description")) {
                text = activity.description ?? ""
            } else if (columnTitle === t("activities.lastEdited")) {
                text = activity.updatedAt ? moment(activity.updatedAt).format(userDateTimeFormat) : ""
            } else if (columnTitle === t("activities.completed")) {
                text = activity.completed
                    ? [
                          getFullName(activity.completedByProfile),
                          activity.completedDate ? moment(activity.completedDate).format(userDateFormat) : ""
                      ]
                          .filter(Boolean)
                          .join(" ")
                    : ""
            } else if (columnTitle === t("activities.dueDate")) {
                text = activity.studentDueDate ? moment(activity.studentDueDate).format(userDateFormat) : ""
            } else if (columnTitle === t("activities.campus")) {
                text = campuses.find((campus) => campus.id === activity.student?.campusId)?.name ?? ""
            } else if (columnTitle === t("activities.program")) {
                text = programs.find((program) => program.id === activity.student?.currentMajor)?.name ?? ""
            } else if (columnTitle === t("activities.startDate")) {
                text = formatDateOnly(activity.student?.startDate, userDateFormat)
            }

            return flatten(text)
        },
        [t, userDateFormat, userDateTimeFormat, campuses, programs]
    )

    const renderIconHtml = React.useCallback((studentActivity: StudentActivity) => {
        const {completed, triggeredWorkflowId, triggeredWorkflow, activity, isPinned} = studentActivity
        const workflowCompleted = triggeredWorkflow?.status === TriggeredWorkflowStatus.Done
        if (!triggeredWorkflowId && !(activity?.isHoldActivity && !completed)) {
            return <div>{!!isPinned && <Icon icon="BOARD_PIN_FILLED" />}</div>
        }
        return (
            <>
                <div
                    className={cx(styles.iconTableWrap, {
                        [styles.red]: activity?.isHoldActivity && !completed && !triggeredWorkflowId,
                        [styles.green]:
                            triggeredWorkflowId && (!activity?.isHoldActivity || completed) && workflowCompleted,
                        [styles.white]:
                            triggeredWorkflowId && (!activity?.isHoldActivity || completed) && !workflowCompleted
                    })}
                    onClick={
                        triggeredWorkflowId
                            ? (e) => {
                                  e.stopPropagation()
                                  showWorkflowDetail(studentActivity)
                              }
                            : undefined
                    }>
                    <Icon
                        icon={
                            triggeredWorkflowId
                                ? "WORKFLOW"
                                : activity?.isHoldActivity && !completed
                                ? "ALERT_CIRCLE"
                                : "EDIT_LINE"
                        }
                        color={
                            activity?.isHoldActivity && !completed && !triggeredWorkflowId
                                ? "#DF1642"
                                : triggeredWorkflowId && workflowCompleted
                                ? "#fff"
                                : "#1E90FF"
                        }
                    />
                </div>
                {!!isPinned && <Icon icon="BOARD_PIN_FILLED" />}
            </>
        )
    }, [])

    const toggleActivity = useMutation(
        async ({
            profileId,
            studentActivityId,
            isCompleted
        }: {
            profileId: number
            studentActivityId: number
            isCompleted: boolean
        }) => {
            const completedDate = new Date()
            const completedBy = BaseService.profileId
            const params = isCompleted
                ? {
                      studentActivityId,
                      completed: true,
                      completedDate,
                      completedBy
                  }
                : {
                      studentActivityId,
                      completed: false
                  }
            await studentActivitiesService.editActivity(params)
            emitEvent(EVENT_STUDENT_ACTIVITY_COMPLETION_TOGGLED, {
                profileId,
                studentActivityId,
                isCompleted,
                completedBy,
                completedDate
            })
        },
        {
            onSuccess: () => getData(),
            onError: (error) => handleError(error)
        }
    )

    const renderCompletedHtml = React.useCallback(
        ({
            studentActivityId,
            profileId,
            completed,
            completedByProfile,
            completedDate,
            triggeredWorkflow,
            initialActivityId
        }) => {
            const workflowCompleted = triggeredWorkflow?.status === TriggeredWorkflowStatus.Done
            if (completed) {
                return (
                    <div className={styles.completedDateWrap}>
                        <div
                            onClick={(e) => {
                                e.stopPropagation()
                                if (toggleActivity.isLoading) return
                                toggleActivity.mutate({profileId, studentActivityId, isCompleted: false})
                            }}>
                            {toggleActivity.isLoading ? (
                                <CircularSpin size="small" />
                            ) : (
                                <Icon
                                    icon="CHECKMARK_CIRCLE"
                                    color={workflowCompleted && !initialActivityId ? "#18A957" : "#1e90ff"}
                                />
                            )}
                        </div>
                        <div className={styles.completedDateInfo}>
                            <p className={styles.completedDateInfo__item}>{getFullName(completedByProfile)}</p>
                            <p className={styles.completedDateInfo__item}>
                                {completedDate ? moment(completedDate).format(userDateFormat) : ""}
                            </p>
                        </div>
                    </div>
                )
            }
            return (
                <div className={styles.completedDateWrap}>
                    <div
                        onClick={(e) => {
                            e.stopPropagation()
                            if (toggleActivity.isLoading) return
                            toggleActivity.mutate({profileId, studentActivityId, isCompleted: true})
                        }}>
                        {toggleActivity.isLoading ? (
                            <CircularSpin size="small" />
                        ) : (
                            <Icon icon="CHECK_CIRCLE" color="#939393" />
                        )}
                    </div>
                </div>
            )
        },
        []
    )

    const getColumns = React.useCallback(() => {
        return [
            {
                title: "icon",
                titleHtml: () => null,
                columnIndex: 1,
                field: "iconHtml",
                style: {width: 24, minWidth: 24, textAlign: "right", padding: 0},
                render: (_, record) => renderIconHtml(record)
            },
            {
                title: t("activities.dateTime"),
                columnIndex: 2,
                field: "activityDate",
                sortable: true,
                orderField: "activityDate",
                render: (value) => moment(value).format(userDateTimeFormat)
            },
            {
                title: t("activities.aging"),
                columnIndex: 3,
                field: "activityDate",
                sortable: true,
                orderField: "activityDate",
                render: (value) => (
                    <span title={moment(value).format(userDateTimeFormat)}>{moment(value).fromNow(true)}</span>
                )
            },
            {
                title: t("activities.activity"),
                columnIndex: 4,
                field: "activity.name",
                sortable: true,
                orderField: "activityName",
                style: {lineHeight: "1.2"}
            },
            {
                title: t("activities.student"),
                columnIndex: 5,
                field: "student",
                sortable: true,
                orderField: "studentName",
                render: (value, record: StudentActivity) => {
                    const departmentId = record.departmentSubunit?.departmentId
                    let url = "#"
                    switch (departmentId) {
                        case BaseDepartmentId.Admissions:
                            url = `/admissions/student/${record.profileId}?tab=${ADMISSION_STUDENT_TABS.ADMISSIONS}&subtab=${AdmissionStudentAdmissionSubtab.Activities}`
                            break
                        case BaseDepartmentId.FinancialAid:
                            url = `/financial-aid/student/${record.profileId}?tab=${FinancialAidStudentTabs.FinancialAid}&subtab=${FinAidStudentFinAidSubtab.Activities}`
                            break
                        case BaseDepartmentId.Academics:
                            url = `/academics/registrar/student/${record.profileId}?tab=${RegistrarStudentTab.Workspace}`
                            break
                        case BaseDepartmentId.AcademicAffair:
                            url = `/academic-affairs/student/${record.profileId}?tab=${StudentServicesStudentTab.Home}&departmentId=${BaseDepartmentId.AcademicAffair}`
                            break
                        case BaseDepartmentId.StudentServices:
                            url = `/student-services/student/${record.profileId}?tab=${StudentServicesStudentTab.Home}&departmentId=${BaseDepartmentId.StudentServices}`
                            break
                    }
                    return <Link to={url}>{getFullName(value)}</Link>
                }
            },
            {
                title: t("activities.assignedTo"),
                columnIndex: 6,
                field: "assignedProfiles",
                style: {lineHeight: "1.2"},
                render: (value) =>
                    (value ?? []).map((profile) => (
                        <div key={profile.profileId}>
                            {getFullName(profile)} {profile.title ? `(${profile.title})` : ""}
                        </div>
                    ))
            },
            {
                title: t("activities.description"),
                columnIndex: 7,
                field: "description",
                sortable: true,
                orderField: "description"
            },
            {
                title: t("activities.lastEdited"),
                columnIndex: 8,
                field: "updatedAt",
                sortable: true,
                orderField: "updatedAt",
                render: (value) => moment(value).format(userDateTimeFormat)
            },
            {
                title: t("activities.completed"),
                columnIndex: 9,
                field: "completedDate",
                sortable: true,
                orderField: "completedDate",
                render: (_, record) => renderCompletedHtml(record)
            },
            {
                title: t("activities.dueDate"),
                columnIndex: 10,
                field: "studentDueDate",
                sortable: true,
                orderField: "studentDueDate",
                render: (value) => value && moment(value).format(userDateFormat)
            },
            {
                title: t("activities.campus"),
                columnIndex: 11,
                field: "student.campusId",
                sortable: false,
                render: (campusId) => campuses.find((campus) => campus.id === campusId)?.name
            },
            {
                title: t("activities.program"),
                columnIndex: 12,
                field: "student.currentMajor",
                sortable: false,
                render: (majorId) => programs.find((p) => p.id === majorId)?.name
            },
            {
                title: t("activities.startDate"),
                columnIndex: 13,
                field: "student.startDate",
                sortable: true,
                orderField: "studentStartDate",
                render: (value) => value && moment(value).format(userDateFormat)
            }
        ]
    }, [renderIconHtml, renderCompletedHtml, props.studentId, props.departmentId, campuses, programs])

    const handleExport = React.useCallback(
        async (type: ExportFileType) => {
            try {
                setIsExporting(true)
                const probe = await studentActivitiesService.getActivities(buildListParams({limit: 1, offset: 0}))
                const exportTotal = probe?.total ?? 0
                const activities: StudentActivity[] = !exportTotal
                    ? []
                    : exportTotal === 1
                    ? probe.data ?? []
                    : (await studentActivitiesService.getActivities(buildListParams({limit: exportTotal, offset: 0})))
                          .data ?? []

                const sourceColumns = columns?.length ? columns : getColumns()
                const visibleTitles = (fields?.length ? fields : sourceColumns.map((column) => column.title)).filter(
                    (title) => title && title !== "icon"
                )
                const payload = {
                    filename: `activities-${moment().format("YYYY-MM-DD")}`,
                    columns: visibleTitles,
                    rows: activities.map((activity) =>
                        visibleTitles.map((title) => formatActivityExportValue(activity, title))
                    )
                }
                if (type === "csv") {
                    exportCsv(payload)
                } else if (type === "excel") {
                    exportExcel(payload)
                }
            } catch (error) {
                handleError(error)
            } finally {
                setIsExporting(false)
            }
        },
        [buildListParams, columns, fields, formatActivityExportValue, getColumns]
    )

    const onClickDelete = React.useCallback(
        async (data) => {
            try {
                await studentActivitiesService.deleteActivity({studentActivityId: [data.studentActivityId]})
                await getData()
                toastSuccess(t("common:activityPopup.deleteSuccess"))
            } catch (error: any) {
                toastError(error.message)
            }
        },
        [getData]
    )

    const onClickClose = React.useCallback(() => {
        setFormEdit(null)
        activityPopup.close()
    }, [])

    const onClickDeleteMulti = React.useCallback(async () => {
        const data = getCurrentData()
        const studentActivityIds = data.filter((activity) => activity.isChecked).map((activity) => activity.id)
        try {
            dispatch({isLoading: true, isShowTableHeaderAction: false})
            await studentActivitiesService.deleteActivity({studentActivityId: studentActivityIds})
            await getData()
        } catch (error: any) {
            toastError(error.message)
        } finally {
            dispatch({isLoading: false})
        }
    }, [getData])

    const onClickEdit = React.useCallback(
        (data) => {
            const departmentIdByActivity = data.departmentSubunit.departmentId
            const canEditActivityByDepartment =
                currentProfilePermissions.departmentActivities.hasPermissionsToAdd(departmentIdByActivity) ||
                model.isAdmin()
            if (!canEditActivityByDepartment) return
            setFormEdit({...formEdit, ...data})
            activityPopup.open()
        },
        [formEdit]
    )

    const onClickPin = React.useCallback(
        async (record: StudentActivity) => {
            const departmentIdByActivity = record.departmentSubunit.departmentId
            const canEditActivityByDepartment =
                currentProfilePermissions.departmentActivities.hasPermissionsToAdd(departmentIdByActivity) ||
                model.isAdmin()
            if (!canEditActivityByDepartment) return
            await studentActivitiesService.setPinned([record.studentActivityId], !record.isPinned)
            await getData()
        },
        [getData]
    )

    const onClickPinMulti = React.useCallback(async () => {
        const data = getCurrentData()
        const studentActivityIds = data.filter((activity) => activity.isChecked).map((activity) => activity.id)
        try {
            dispatch({isLoading: true, isShowTableHeaderAction: false, isHideMenuActions: true})
            await studentActivitiesService.setPinned(studentActivityIds, true)
            await getData()
        } catch (error: any) {
            toastError(error.message)
        } finally {
            dispatch({isLoading: false})
        }
    }, [getData])

    const handleRowClick = React.useCallback(
        (record: StudentActivity) => {
            onClickEdit(record)
        },
        [onClickEdit]
    )

    const handleCanDeleteRow = (data) => {
        props.onUpdateRowData(data, data)
    }

    const getTableHeaderActions = React.useCallback(
        (isShowDuplicateBtn = true, checkedData = []) => {
            const actions = [
                !isFullDisplay
                    ? null
                    : {
                          title: t(isExpanded ? "common:action.collapseAll" : "common:action.expandAll"),
                          icon: "EXPAND",
                          action: () => setExpanded((prev) => !prev)
                      }
            ]
            if (checkedData?.length) {
                if (checkIfCanDeleteActivity(checkedData[0]?.departmentSubunit?.departmentId)) {
                    actions.push({
                        title: t("common:action.pin"),
                        icon: "BOARD_PIN_FILLED",
                        action: () => onClickPinMulti()
                    })
                    actions.push({
                        title: t("common:action.delete"),
                        icon: "DELETE",
                        action: () => onClickDeleteMulti()
                    })
                }
            }
            return actions.filter(Boolean)
        },
        [onClickDeleteMulti, isFullDisplay, isExpanded]
    )

    useEffect(() => {
        dispatch({isClassComponent: false, isHideMenuActions: true})
        dispatchFunc([
            {key: "getPageTitle", func: () => t("activities.title")},
            {key: "getFilterMemoryKey", func: getFilterMemoryKey},
            {key: "getListData", func: getData},
            {key: "getFields", func: getFields},
            {key: "getColumns", func: getColumns},
            {key: "onClickEdit", func: onClickEdit},
            {key: "onClickDelete", func: onClickDelete},
            {key: "onClickRowItem", func: handleRowClick},
            {key: "getTableHeaderActions", func: getTableHeaderActions}
        ])
    }, [getColumns, getTableHeaderActions, handleRowClick, onClickDelete, onClickEdit])

    useEffect(() => {
        if (isLoadedTableFuncs) {
            getData()
        }
    }, [
        isLoadedTableFuncs,
        page,
        pageSize,
        orderField?.field,
        orderField?.order,
        selectedDepartmentId,
        props.studentId,
        appliedFilter,
        advancedFilter
    ])

    const getStudentDueText = (studentActivity: StudentActivity) => {
        return studentActivity.studentDue.unit === "custom"
            ? `at ${moment(studentActivity.studentDue.time).format(userDateTimeFormat)}`
            : `in ${studentActivity.studentDue.value} ${studentActivity.studentDue.unit} from ${moment(
                  studentActivity.updatedAt
              ).format(userDateTimeFormat)}` +
                  (studentActivity.studentDueDate
                      ? ` (at ${moment(studentActivity.studentDueDate).format(userDateTimeFormat)})`
                      : "")
    }

    const renderItemsInRow = () => {
        if (isLoading) {
            return (
                <div className={styles.rowLoading}>
                    <Spin />
                </div>
            )
        }

        return (
            <>
                {data.map((item: StudentActivity) => {
                    return (
                        <div key={item.studentActivityId} className={styles.rowWrap}>
                            <div className={styles.rowTitleWrap}>
                                <div>{renderIconHtml(item)}</div>
                                <div>{item.activity?.name || ""}</div>
                                <div className="flex-1" />

                                <div
                                    className={cx(styles.activityIcon, "cursor-pointer")}
                                    onClick={() => onClickPin(item)}>
                                    <Icon icon={item.isPinned ? "BOARD_PIN_FILLED" : "BOARD_PIN"} />
                                </div>
                                <div
                                    className={cx(styles.activityIcon, "cursor-pointer")}
                                    onClick={() => onClickEdit(item)}>
                                    <Icon icon="EDIT_LINE" />
                                </div>
                                {canDeleteActivity && (
                                    <div
                                        className={cx(styles.activityIcon, "cursor-pointer")}
                                        onClick={() => onClickDelete(item)}>
                                        <Icon icon="DELETE" />
                                    </div>
                                )}
                            </div>
                            <div className={styles.rowDescription}>{item.description}</div>
                            <div className={styles.rowFieldWrap}>
                                <div className={styles.rowFieldTitle}>Created by</div>
                                <div className={styles.rowFieldValue}>{getFullName(item.createdByProfile)}</div>
                            </div>
                            <div className={styles.rowFieldWrap}>
                                <div className={styles.rowFieldTitle}>Date & time</div>
                                <div className={styles.rowFieldValue}>
                                    {moment(item.activityDate).format(userDateTimeFormat)}
                                </div>
                            </div>
                            {!!item.assignedProfiles?.length && (
                                <div className={styles.rowFieldWrap}>
                                    <div className={styles.rowFieldTitle}>Assigned To</div>
                                    <div className={styles.rowFieldValue}>
                                        {(item.assignedProfiles ?? []).map((profile) => (
                                            <div key={profile.profileId}>
                                                {getFullName(profile)} {profile.title ? `(${profile.title})` : ""}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div className={styles.rowFieldWrap}>
                                <div className={styles.rowFieldTitle}>Completed</div>
                                <div
                                    className={cx(styles.rowFieldValue, styles.rowStatusValue, {
                                        [styles.rowCompletedValue]: item.completed
                                    })}>
                                    {item.completed ? (
                                        <>
                                            <div
                                                className={styles.rowStatusIcon}
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    if (toggleActivity.isLoading) return
                                                    toggleActivity.mutate({
                                                        profileId: item.profileId,
                                                        studentActivityId: item.studentActivityId,
                                                        isCompleted: false
                                                    })
                                                }}>
                                                {toggleActivity.isLoading ? (
                                                    <CircularSpin size="small" />
                                                ) : (
                                                    <Icon
                                                        icon="CHECKMARK_CIRCLE"
                                                        color="#18A957"
                                                        className={styles.rowActivityIcon}
                                                    />
                                                )}
                                            </div>
                                            <div className={styles.rowStatusValue}>
                                                {item.completedDate
                                                    ? moment(item.completedDate).format(userDateTimeFormat)
                                                    : "-"}
                                                <br />
                                                by {getFullName(item.completedByProfile)}
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div
                                                className={styles.rowStatusIcon}
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    if (toggleActivity.isLoading) return
                                                    toggleActivity.mutate({
                                                        profileId: item.profileId,
                                                        studentActivityId: item.studentActivityId,
                                                        isCompleted: true
                                                    })
                                                }}>
                                                {toggleActivity.isLoading ? (
                                                    <CircularSpin size="small" />
                                                ) : (
                                                    <Icon
                                                        icon="CHECK_CIRCLE"
                                                        color="#939393"
                                                        className={styles.rowActivityIcon}
                                                    />
                                                )}
                                            </div>
                                            <div className={styles.rowStatusValue}>Not completed</div>
                                        </>
                                    )}
                                </div>
                            </div>
                            {!!item.studentDue && (
                                <div className={styles.rowFieldWrap}>
                                    <div className={styles.rowFieldTitle}>Set due alert</div>
                                    <Tooltip placement="left" title={getStudentDueText(item)}>
                                        <div className={styles.rowFieldValue}>
                                            {item.studentDue.unit === "custom"
                                                ? `at ${moment(item.studentDue.time).format(userDateTimeFormat)}`
                                                : `in ${item.studentDue.value} ${item.studentDue.unit} ${
                                                      item.studentDue.time
                                                          ? `at ${moment(item.studentDue.time).format(userTimeFormat)}`
                                                          : ""
                                                  }`}
                                        </div>
                                    </Tooltip>
                                </div>
                            )}
                        </div>
                    )
                })}
            </>
        )
    }

    const handleActivityUpserted = React.useCallback(
        (activity: StudentActivity) => {
            getData()
        },
        [props.studentId, getData]
    )

    const renderFilter = () => {
        const canAddActivity =
            model.isAdmin() ||
            (model.isStaff() &&
                (!selectedDepartmentId ||
                    currentProfilePermissions.departmentActivities.hasPermissionsToAdd(selectedDepartmentId)))

        return (
            <StudentActivityFilters
                isCompact={!isFullDisplay}
                filterKey={filterKey}
                departmentId={props.departmentId ?? selectedDepartmentId}
                onApply={onApplyFilter}
                onClear={onClearFilter}
                onAddClick={
                    model.isStaffOrAdmin() && canAddActivity
                        ? () => {
                              if (props.addNewOpenPage) history.push("/activities/new")
                              else activityPopup.open()
                          }
                        : undefined
                }
                onExport={handleExport}
                isExporting={isExporting}
            />
        )
    }

    const renderContent = (departmentId?: BaseDepartmentId) => {
        const isActive =
            departmentId === selectedDepartmentId &&
            (currentProfilePermissions.departmentActivities.hasPermissionsToView(selectedDepartmentId) ||
                model.isAdmin())
        return (
            <div>
                {model.isStaffOrAdmin() && renderFilter()}
                {isActive && isFullDisplay && (
                    <ActivityStats
                        isVisible={activityPopup.isVisible}
                        departmentId={departmentId}
                        studentId={props.studentId}
                        advancedFilter={advancedFilter}
                        setAdvancedFilter={setAdvancedFilter}
                    />
                )}

                <KlassappTableHeader
                    page={page}
                    total={total}
                    defaultPageSize={pageSize}
                    isShowAction
                    actions={tableHeaderActions}
                    onChangePage={props.onChangePage}
                    onChangeRowPerPage={props.onChangeRowPerPage}
                    displayMode={displayMode}
                    fields={fields}
                    unselectableFields={["icon"]}
                    allFields={allFields}
                    onChangeFields={props.onChangeFields}
                    onChangeAllFields={props.onChangeAllFields}
                    onDraggableColumn={props.onDraggableColumn}
                />
                {isFullDisplay ? (
                    <KlassappTable
                        columns={columns}
                        data={data}
                        isLoading={isLoading}
                        menuActions={[]}
                        fields={fields}
                        allFields={allFields}
                        unselectableFields={["icon"]}
                        orderField={orderField}
                        isShowCheckedColumn
                        onClickRowItem={props.onClickRowItem}
                        onToggleCheckBox={handleCanDeleteRow}
                        onChangeFields={props.onChangeFields}
                        onUpdateRowData={props.onUpdateRowData}
                        onUpdateTableData={props.onUpdateTableData}
                        onDraggableColumn={props.onDraggableColumn}
                        onChangeAllFields={props.onChangeAllFields}
                        onClickSortColumn={props.onClickSortColumn}
                        expandable={{
                            isExpanded,
                            rowExpandable: () => true,
                            expandedRowRender: (record: StudentActivity, expanded: boolean) =>
                                !expanded ? null : !!record.workflowActivities?.length ? (
                                    <WorkflowActivitiesTable
                                        activity={record}
                                        studentId={props.studentId}
                                        departmentId={selectedDepartmentId}
                                        getData={getData}
                                        parentFields={fields}
                                        parentAllFields={allFields}
                                    />
                                ) : (
                                    <div className="px-8 py-8">
                                        <div className="mb-4">
                                            <b>Department</b>: {record.departmentSubunit?.name}
                                        </div>
                                        <div className="mb-4">
                                            <b>Notify</b>:{" "}
                                            {record.notifyStaffRole
                                                ? record.notifyStaffRoleIds.join(", ")
                                                : record.notifyStaffProfiles.map(getFullName).join(", ")}
                                        </div>
                                        {record.completed && (
                                            <div className="mb-4">
                                                <b>Completed</b>: {getFullName(record.completedByProfile)} (
                                                {moment(record.completedDate).format(userDateTimeFormat)})
                                            </div>
                                        )}
                                        {record.studentDue && (
                                            <div className="mb-4">
                                                <b>Set due alert</b>: {getStudentDueText(record)}
                                            </div>
                                        )}
                                        {!!record.triggeredWorkflow && (
                                            <div className="mb-4">
                                                <b>Initiate worklow</b>: {record.triggeredWorkflow?.workflow?.name}
                                            </div>
                                        )}
                                        <div className="mb-4">
                                            <b>Last edited</b>: {moment(record.updatedAt).format(userDateTimeFormat)}
                                        </div>
                                        <div className="mb-4 pre-wrap">
                                            <b>Description</b>:<br />
                                            {record.description}
                                        </div>
                                    </div>
                                )
                        }}
                        tableClassName={styles.activitiesTable}
                        classNameActionMenu={styles.tableActionMenu}
                    />
                ) : (
                    renderItemsInRow()
                )}
                {!!activityPopup.isVisible && (
                    <ActivityPopup
                        isShow={activityPopup.isVisible}
                        onClose={onClickClose}
                        studentId={props.studentId}
                        departmentId={selectedDepartmentId}
                        setReload={handleActivityUpserted}
                        formEditData={formEdit}
                    />
                )}

                <ProcessWorkflowModal
                    visible={!!workflowActivity}
                    onClose={() => setWorkflowActivity(undefined)}
                    activity={workflowActivity}
                />
            </div>
        )
    }

    const renderBody = (module: ActivitiesTab) => {
        return (
            <TabPane tab={<TabHeader title={module.name} />} key={module.tabKey}>
                {renderContent(module.departmentId)}
            </TabPane>
        )
    }

    if (departmentId) {
        return (
            <Tabs className="fullwidth klassSubTab" activeKey={activeTabKey} onChange={setActiveTabKey}>
                {modules.map((module) => renderBody(module))}
            </Tabs>
        )
    }

    return renderContent()
}

const WrappedActivitiesTable = KlassappTableHOC(ActivitiesTable)

export default WrappedActivitiesTable
